import { CheckIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button, Divider, Flex, Heading, HStack, List, ListItem,
  Modal,
  ModalBody, ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text, Tooltip,
  useColorMode, useColorModeValue,
  useToast,
  VStack
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import SidebarWithHeader from "components/SidebarWithHeader";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetPlansQuery, useGetSubscriptionByIdQuery, useGetSubscriptionsQuery, useUpgradePlanMutation } from "store/Subscription/SubscriptionApi";

// Rank tiers for comparisons (lower number = lower tier)
const getTierRank = (name) => {
  if (!name) return -1;
  const key = name.toLowerCase();
  if (key.includes("starter")) return 1;
  if (key.includes("pro")) return 2;
  if (key.includes("enterprise")) return 3; // usually no priceId -> disabled anyway
  return 0; // unknown tier -> treat as lowest
};

export default function PricingPlans() {
  const navigate = useNavigate();
  const toast = useToast();
  const { colorMode } = useColorMode();
  const textColor = useColorModeValue("brand.900", "brand.500");
  const whiteColor = useColorModeValue("black", "white");
  const tabSelectedText = useColorModeValue("white", "white");
  const navBackground = useColorModeValue("purple.500", "brand.500");
  const [selectedTab, setSelectedTab] = useState("yearly");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const { data: subscriptionList, isLoading: isSubscriptionListLoading, isFetching: isFetchingSubscriptionList, refetch: refetchSubscriptionList } = useGetSubscriptionsQuery();
  const [updgradePlan, { isLoading: isUpgrading }] = useUpgradePlanMutation();
  const { data: plansData, isLoading: isPlansLoading } = useGetPlansQuery({ planType: 'subscription'});
  
  // Derive the "current" subscription ID from the list (non-canceled)
  const listCurrent = useMemo(
    () => (subscriptionList?.data?.length ? subscriptionList.data.find(sub => sub.status !== 'canceled') || null : null),
    [subscriptionList]
  );
  const subscriptionIdToFetch = listCurrent?._id || null;

  const {
    data: subscriptionById,
    isLoading: isSubscriptionLoading,
  } = useGetSubscriptionByIdQuery(
    subscriptionIdToFetch ? subscriptionIdToFetch : skipToken
  );
  
  const currentSubscription = subscriptionById ?? null;
  const subscriptionStatus = currentSubscription?.status;
  const isTrialing = subscriptionStatus === "trialing";
  const isActive = subscriptionStatus === "active";

  // current subscription references
  const currentPlanRef = currentSubscription?.planRef || null;
  const currentPriceId = currentPlanRef?.priceId ?? null;
  const currentBillingCycle = currentPlanRef?.billingCycle ?? null; 
  const currentTierRank = getTierRank(currentPlanRef?.name);

// treat active + trialing as same for gating
const hasSubscription = !!currentSubscription;

// Is this card the current plan?
const isCurrentPlan = (plan) => !!currentPriceId && plan?.priceId === currentPriceId && hasSubscription;

// Enable only higher tier in SAME billing cycle when you already have a plan.
// If no subscription -> any purchasable plan enabled.
const isPlanEnabled = (plan) => {
  if (!plan?.priceId) return false; // Enterprise or Coming soon -> disabled
  if (!hasSubscription) return true; // no sub -> free to pick anything

  // current plan stays disabled
  if (isCurrentPlan(plan)) return false;

    // Block upgrades while on trial
    if (isTrialing) return false;

  // must match billing cycle (monthly->monthly, yearly->yearly)
  const sameCycle = currentBillingCycle && plan.billingCycle === currentBillingCycle;

  // only allow upgrade to strictly higher tier
  const targetRank = getTierRank(plan.name);
  const isHigherTier = targetRank > currentTierRank;

  return sameCycle && isHigherTier;
};

const getButtonLabel = (plan) => {
  if (!plan?.priceId) return plan?.button || "Coming soon";

  if (isCurrentPlan(plan)) {
    if (subscriptionStatus === "trialing") {
      return "Your Current Plan — Free Trial";
    } else if (subscriptionStatus === "active") {
      return "Your Current Plan — Active";
    } else if (subscriptionStatus === "canceled") {
      return "Your Plan — Canceled";
    }
  }

  return plan?.button || `Get ${plan.name}`;
};

  // Allowed billing cycles
  const allowedCycles = isActive && currentBillingCycle
    ? [currentBillingCycle]
    : ["monthly", "yearly"];

  useEffect(() => {
    if (!allowedCycles.includes(selectedTab)) {
      setSelectedTab(allowedCycles[0]);
    }
  }, [allowedCycles, selectedTab]);

  // Transform API plans -> UI model
  const plans = useMemo(() => {
    if (!plansData) return [];
    return plansData?.map((plan) => {
      const pctOff =
        plan.originalPrice && plan.originalPrice > plan.price
          ? Math.round(((plan.originalPrice - plan.price) / plan.originalPrice) * 100)
          : null;

      return {
        name: plan.name,
        note: plan.description || "",
        price: `$${Number(plan.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
        description: plan.billingCycle === "yearly" ? "Billed yearly" : "Billed monthly",
        originalPrice: plan.originalPrice
          ? `$${Number(plan.originalPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
          : null,
        badge: pctOff ? `${pctOff}% OFF` : null,
        limitedOffer: plan.name === "Pro",
        button: "Get Started",
        priceId: plan.priceId,
        features: plan.features || [],
        billingCycle: plan.billingCycle,
        // extra limits for the table panel
        imageVariations: plan.imageVariations,
        imageBackground: plan.imageBackground,
        videosCredits: plan.videosCredits,
      };
    });
  }, [plansData]);

  // Separate Monthly / Yearly
  const moveStarterFirst = (plansArr) => {
    if (!plansArr) return [];
    const starter = plansArr.find((p) => p.name === "Starter");
    const others = plansArr.filter((p) => p.name !== "Starter");
    return starter ? [starter, ...others] : plansArr;
  };
  
  const makeEnterprise = (billingCycle) => ({
    name: "Enterprise",
    note:
      "For ad wizards handling high-volume madness, with custom workflows, dedicated support, and enterprise-grade security.",
    price: "Contact Us",
    description: billingCycle === "yearly" ? "Billed yearly" : "Billed monthly",
    button: "Coming soon",
    features: [
      "API Volume Based Discount",
      "Commercial Terms",
      "Top Security & Privacy",
      "Done-for-You Creative Service",
      "Dedicated Account Manager",
      "24/7 Priority Customer Support",
    ],
    // important for your component:
    priceId: null,                
    badge: null,
    limitedOffer: false,
    originalPrice: null,
    billingCycle,
    features: [
      "API Volume Based Discount",
      "Commercial Terms",
      "Top Security & Privacy",
      "Done-for-You Creative Service",
      "Dedicated Account Manager",
      "24/7 Priority Customer Support",
    ],
  });
  
  const appendEnterpriseLast = (plansArr, billingCycle) => {
    const exists = plansArr.some((p) => p.name === "Enterprise");
    return exists ? plansArr : [...plansArr, makeEnterprise(billingCycle)];
  };
  
  const monthlyPlans = useMemo(() => {
    const filtered = plans.filter((p) => p.billingCycle === "monthly");
    const starterFirst = moveStarterFirst(filtered);
    return appendEnterpriseLast(starterFirst, "monthly");
  }, [plans]);
  
  const yearlyPlans = useMemo(() => {
    const filtered = plans.filter((p) => p.billingCycle === "yearly");
    const starterFirst = moveStarterFirst(filtered);
    return appendEnterpriseLast(starterFirst, "yearly");
  }, [plans]);


  const handleUpgradeConfirm = () => {
    if (!selectedPlan?.priceId || !currentSubscription?.subscriptionId) return;
  
    const payload = {
      priceId: selectedPlan.priceId,
      billingCycle: selectedPlan?.billingCycle,
      name: selectedPlan?.name
    };

    updgradePlan({ payload, subscriptionId: currentSubscription?.subscriptionId })
      .unwrap()
      .then((res) => {
        const meta = res?.data?.metadata || {};
        const upgraded = String(meta?.upgraded || "").toLowerCase() === "true";
        const expectedPlan = (meta?.planName || "").toLowerCase(); // "pro", "starter", etc.
      
        toast({
          position: "top-right",
          title: "Upgraded successfully",
          description:
            res?.message ||
            res?.data?.message ||
            `You're now on ${meta?.planName || selectedPlan?.name}.`,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      
        setConfirmOpen(false);
        setSelectedPlan(null);
      
        // store for Subscription page to poll
        if (upgraded && expectedPlan) {
          sessionStorage.setItem("expectedPlanAfterUpgrade", expectedPlan);
      
          navigate("/subscription", {
            state: {
              refreshSubs: true,
              expectPlan: expectedPlan,
              ts: Date.now(),
            },
            replace: true,
          });
        } else {
          // fallback: still navigate, but without expecting a plan change
          navigate("/subscription", {
            state: { refreshSubs: true, ts: Date.now() },
            replace: true,
          });
        }
      })
      
      .catch((err) => {
        toast({
          position: "top-right",
          title: "Upgrading failed",
          description: err?.message || err?.data?.message || "Something went wrong while upgrading.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };  

  const isLoadingAny = isPlansLoading || isSubscriptionListLoading || isSubscriptionLoading;

  const renderPlanCard = (plan) => (
    <Box key={plan.priceId || plan.name} pos="relative" role="group" display="flex" flexDirection="column">
      <Flex
        flexDirection="column"
        flexGrow="1"
        py={4}
        px={{ base: 6, md: 10 }}
        position="relative"
        zIndex={10}
        bg={colorMode === "light" ? "white" : "background.dark"}
        border="1px solid"
        borderColor="mediumPurple"
        borderRadius="20px"
        transition="all 0.3s ease"
        justifyContent="space-between"
        minHeight={{ base: "200px", md: "580px" }}
        _groupHover={{ transform: "translate(-10px, -10px)" }}
        rowGap={{ base: 2, md: 0 }}
      >
        <Box>
          {plan.badge && (
            <Badge colorScheme="purple" position="absolute" top={3} right={3} fontSize="0.7rem" px={2} py={1} rounded="md">
              {plan.badge}
            </Badge>
          )}
          {plan.limitedOffer && (
            <Box
              position="absolute"
              top="-25px"
              right="20px"
              bgGradient="linear(to-b, #FFD400, #FF61C6, #A155FF, #4BC2FF)"
              px={2}
              py={1}
              fontSize="0.7rem"
              fontWeight="bold"
              textTransform="uppercase"
              borderTopRadius="md"
              color="white"
              boxShadow="0 0 6px rgba(255, 86, 246, 0.6)"
              zIndex={1}
            >
              Recommended
            </Box>
          )}

          <Text fontWeight="bold" fontSize="md" mb={2}>
            {plan.name}
          </Text>

          <Flex flexDirection="row" columnGap="20px" align="baseline">
            {plan.originalPrice && (
              <Text fontSize={{ base: "md", md: "xl" }} textDecoration="line-through" color={whiteColor}>
                {plan.originalPrice}
              </Text>
            )}
            <Text fontSize={{ base: "md", md: "xl" }} fontWeight="bold" color={whiteColor}>
              {plan.price}
            </Text>
          </Flex>

          <Flex mb="10px" gap={2} alignItems="center">
            {plan.description && (
              <Text fontSize="14px">
                {plan.description}
              </Text>
            )}
            {plan?.name === "Enterprise" ? null : (
              <Tooltip
                borderRadius={5}
                label={
                  <Box fontSize="sm" p="5px">
                    <Text mb={1}>Free plan includes:</Text>
                    <List spacing={1} fontSize="sm">
                      <ListItem>• 20 Background Changes</ListItem>
                      <ListItem>• 20 Creative Variations</ListItem>
                      <ListItem>• 4 Video Creation Credits</ListItem>
                      <ListItem>• Bulk Upload</ListItem>
                      <ListItem>• Bulk Campaign Creator</ListItem>
                      <ListItem>• Bot Nullifier</ListItem>
                      <ListItem>• Bid Multiplier</ListItem>
                      <ListItem>• AI Image</ListItem>
                      <ListItem>• AI Video</ListItem>
                      <ListItem>• Ad Rules</ListItem>
                    </List>
                  </Box>
                }
                fontSize="md"
              >
                <InfoOutlineIcon pointerEvents="all" />
              </Tooltip>
            )}
          </Flex>

          <Button
            w="full"
            colorScheme="brand"
            isDisabled={!isPlanEnabled(plan)}
            onClick={() => {
              if(hasSubscription){
                if (!isPlanEnabled(plan)) return;
                setSelectedPlan(plan);
                setConfirmOpen(true);
              }else{
                localStorage.setItem("allowPricingEntry", "true");
                navigate(
                  `/pricing/${plan.name}?billing=${plan.billingCycle}&priceId=${plan.priceId}`
                );
              }
            }}
          >
            {getButtonLabel(plan)}
          </Button>

          <Divider mt="15px" colorScheme="brand" />

          <Text mt="10px" mb="10px" fontSize="14px" fontWeight="bold">
            Includes:
          </Text>

          <VStack align="start" spacing={2} mb={4}>
            {plan.features.map((feature, idx) => (
              <HStack key={`${plan.priceId}-${idx}`} alignItems="flex-start">
                <Box>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="green.500"
                    color="white"
                    borderRadius="3px"
                    w="4"
                    h="4"
                  >
                    <CheckIcon boxSize={3} />
                  </Box>
                </Box>
                <Text fontSize={{ base: "xs", md: "sm" }} color={whiteColor}>
                  {feature}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        <Box minH={{ base: "30px", md: "90px" }}>
          <Divider orientation="horizontal" mb={4} />
          <Text fontSize="xs" color="gray.500">
            {plan.note}
          </Text>
        </Box>
      </Flex>

      {/* Hover gradient border */}
      <Box
        pos="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        borderRadius="20px"
        zIndex={1}
        _before={{
          content: '""',
          pos: "absolute",
          top: "10px",
          left: "10px",
          right: "0px",
          bottom: "0px",
          borderRadius: "20px",
          bg: "linear-gradient(90deg, rgba(56,187,230,1) 0%, rgba(254,113,255,1) 46%, rgba(237,100,121,1) 63%, rgba(247,235,13,1) 99%)",
          opacity: 0,
          transition: "all 0.3s ease",
          zIndex: -1,
        }}
        _groupHover={{
          _before: { opacity: 1, top: "8px", left: "8px" },
        }}
      />
    </Box>
  );

  const renderPlans = (list) => {
    if (isLoadingAny) {
      return (
        <Flex direction={'column'} gap={2} alignItems={'center'} justifyContent={'center'} h={'50vh'}>
          <Spinner />
          <Text color={whiteColor}>Loading plans…</Text>
        </Flex>
      );
    }
    if (!list?.length) {
      return (
        <Flex direction={'column'} gap={2} alignItems={'center'} justifyContent={'center'} h={'50vh'}>
          <Text color={whiteColor}>No plans available.</Text>
        </Flex>
      );
    }

    return (
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={8}>
        {list.map((plan) => renderPlanCard(plan))}
      </SimpleGrid>
    );
  };

  return (
    <SidebarWithHeader>
      {/* Confirm & Pay modal */}

      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} isCentered size={'xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Changes</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPlan ? (
              <VStack align="start" spacing={2}>
                <Text>
                  Plan: <b>{selectedPlan.name}</b>
                </Text>
                {selectedPlan.price && (
                  <Text>
                    Price: <b>{selectedPlan.price}</b>
                  </Text>
                )}
                <Text>
                  Billing:{" "}
                  <b>
                    {selectedPlan.billingCycle === "yearly" ? "Yearly" : "Monthly"}
                  </b>
                </Text>
                {selectedPlan.badge && (
                  <Badge colorScheme="purple">{selectedPlan.badge}</Badge>
                )}
                <Text fontSize="sm" color="gray.500">
                  You can change or cancel later.
                </Text>
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              isDisabled={isUpgrading}
              size={'sm'}
            >
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              isLoading={isUpgrading}
              size={'sm'}
              onClick={handleUpgradeConfirm} 
            >
              Pay now
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box minH="100vh">
        <Box spacing={2} textAlign="left" mb="20px" align="start">
          <Heading fontSize="3xl" color={textColor}>
            Our Plans
          </Heading>
          <Text fontSize="lg" color={textColor}>
            Start free. Upgrade fast. Stay stress-free.
          </Text>
        </Box>

        <Tabs
            colorScheme="brand"
            variant="solid-rounded"
            size="sm"
            position="relative"
            index={Math.max(0, allowedCycles.indexOf(selectedTab))}
            onChange={(index) => {
              // If only one tab is available, do nothing
              if (allowedCycles.length === 1) return;
              setSelectedTab(allowedCycles[index] || allowedCycles[0]);
            }}
            isLazy
          >
            <Flex justify="space-between" mb={4}>
              <TabList gap="10px">
                {allowedCycles.includes("monthly") && (
                  <Tab
                    borderWidth={1}
                    borderRadius={5}
                    colorScheme="brand"
                    border={textColor}
                    fontWeight="light"
                    _selected={{ color: tabSelectedText, bg: navBackground }}
                  >
                    Monthly
                  </Tab>
                )}
                {allowedCycles.includes("yearly") && (
                  <Tab
                    borderWidth={1}
                    borderRadius={5}
                    colorScheme="brand"
                    border={textColor}
                    fontWeight="light"
                    _selected={{ color: tabSelectedText, bg: navBackground }}
                  >
                    Yearly
                  </Tab>
                )}
              </TabList>
            </Flex>

            {/* Panels */}
            {selectedTab === "monthly" && renderPlans(monthlyPlans)}
            {selectedTab === "yearly" && renderPlans(yearlyPlans)}
        </Tabs>

      </Box>
    </SidebarWithHeader>
  );
}